import React, {
  memo, useCallback, useMemo, useState,
} from 'react'

import {
  Checkbox, Button, Icon, ThumbUp, PersonSingle,
} from '@cgtrader/triangle-ui/dist/src/ssr-index'

import FilterSearchInput from 'Pods/SearchPage/Components/Shared/FilterSearchInput/FilterSearchInput'
import { addDesigner, removeDesigner } from 'Pods/SearchPage/Store/actions/designers'
import { useSearchPageStore } from 'Pods/SearchPage/Store/index'
import I18n from 'Pods/Shared/I18n'

import { EMPTY_ARRAY } from 'Utils/stableDefaults'

import { MOCK_DESIGNERS, DESIGNER_SCOPES } from './constants'

const translate = (key, options) => I18n.t(key, { ns: 'rails', ...options })

const DEFAULT_LIMIT = 3

function DesignerContent() {
  const designers = useSearchPageStore(state => state.designers ?? EMPTY_ARRAY)
  const [query, setQuery] = useState('')
  const [activeScopes, setActiveScopes] = useState(EMPTY_ARRAY)
  const [showAll, setShowAll] = useState(false)

  const handleChange = useCallback((username, isChecked) => {
    if (isChecked) {
      addDesigner(username)
    } else {
      removeDesigner(username)
    }
  }, [])

  // Scope chips are visual-only for now (no real filtering until the backend exposes the
  // following/hidden/top designer sets).
  const toggleScope = useCallback((key) => {
    setActiveScopes(prev => (prev.includes(key) ? prev.filter(s => s !== key) : [...prev, key]))
  }, [])

  const visibleDesigners = useMemo(() => {
    const normalized = query.trim().toLowerCase()
    if (!normalized) return MOCK_DESIGNERS
    return MOCK_DESIGNERS.filter(designer => designer.name.toLowerCase().includes(normalized))
  }, [query])

  const displayedDesigners = showAll ? visibleDesigners : visibleDesigners.slice(0, DEFAULT_LIMIT)
  const hasMore = visibleDesigners.length > DEFAULT_LIMIT

  return (
    <div className="designer-content">
      <FilterSearchInput
        id="designer-search"
        value={query}
        onChange={setQuery}
        placeholder={translate('search_page.filters.designers.search_placeholder')}
      />

      <div className="designer-content__scopes">
        {DESIGNER_SCOPES.map(({ key, icon }) => {
          const isActive = activeScopes.includes(key)
          return (
            <Button
              key={key}
              variant="with-icon"
              icon={<Icon notSpaced svgComponent={icon} size="small" color={isActive ? 'white' : 'dark'} />}
              isActive={isActive}
              type={isActive ? 'primary' : 'secondary'}
              size="small"
              customClass="designer-content__scope"
              onClick={() => toggleScope(key)}
            >
              {translate(`search_page.filters.designers.${key}`)}
            </Button>
          )
        })}
      </div>

      <div className="designer-content__list">
        {displayedDesigners.map(designer => (
          <Checkbox
            key={designer.username}
            id={`designer-${designer.username}`}
            name={`designer-${designer.username}`}
            isControlled
            isChecked={designers.includes(designer.username)}
            onChange={isChecked => handleChange(designer.username, isChecked)}
            // Name lives in the checkbox label so the input has an accessible name; the
            // stats sit on a second line below it.
            label={(
              <span className="designer-content__info">
                <span className="designer-content__name">{designer.name}</span>
                <span className="designer-content__meta cgt-caption">
                  <span className="designer-content__models cgt-caption-semibold">
                    {translate('search_page.filters.designers.models_count', { count: designer.modelsCount })}
                  </span>
                  <span className="designer-content__rating cgt-caption-semibold">
                    <Icon notSpaced svgComponent={ThumbUp} size="small" color="dark" />
                    {`${designer.ratingPercent}%`}
                  </span>
                </span>
              </span>
            )}
            trailLabel={designer.isFollowing ? (
              <Icon
                notSpaced
                svgComponent={PersonSingle}
                size="small"
                color="dark"
                aria-label={translate('search_page.filters.designers.following')}
              />
            ) : undefined}
          />
        ))}
      </div>

      {hasMore && (
        <Button
          type="ghost-primary"
          customClass="designer-content__show-more cgt-link-primary"
          onClick={() => setShowAll(value => !value)}
        >
          {translate(`search_page.filters.designers.${showAll ? 'show_less' : 'show_more'}`)}
        </Button>
      )}
    </div>
  )
}

export default memo(DesignerContent)
